import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { BookOpen, Calendar, MessageSquare, ArrowLeft, ArrowRight, ExternalLink } from 'lucide-react';
import { ageBooks, ageBookDetails } from '../data/mock';
import NotifyMeModal from '../components/NotifyMeModal';
import BookReviews from '../components/BookReviews';

const BookDetailPage = () => {
  const { age } = useParams();
  const book = ageBooks.find((b) => b.age === parseInt(age));
  const bookDetails = ageBookDetails[parseInt(age)];
  const [isNotifyModalOpen, setIsNotifyModalOpen] = useState(false);

  if (!book) {
    return (
      <div className="pt-20">
        <div className="container py-12 text-center">
          <h1 className="heading-1 mb-4">Book not found</h1>
          <Link to="/books" className="btn-primary">
            View All Books
          </Link>
        </div>
      </div>
    );
  }

  const previousBook = ageBooks.find((b) => b.age === book.age - 1);
  const nextBook = ageBooks.find((b) => b.age === book.age + 1);

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="section" style={{ background: 'var(--gradient-hero-subtle)', paddingTop: '4rem' }}>
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              {/* Book Cover Placeholder */}
              <div className={`${book.color} rounded-lg p-12 flex items-center justify-center hover-lift`}>
                <div className="text-center">
                  <div className="w-32 h-32 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: 'rgba(255, 255, 255, 0.6)' }}>
                    <span className="text-6xl font-bold" style={{ color: 'var(--text-primary)' }}>
                      {book.age}
                    </span>
                  </div>
                  <h3 className="heading-2">{book.title}</h3>
                </div>
              </div>

              {/* Book Info */}
              <div>
                <h1 className="heading-1 mb-4">
                  {bookDetails ? `Age ${book.age}: ${bookDetails.title}` : book.title}
                </h1>
                <p className="body-large mb-6" style={{ color: 'var(--text-secondary)' }}>
                  {bookDetails ? bookDetails.tagline : book.focus}
                </p>
                <p className="body-medium mb-6" style={{ color: 'var(--text-secondary)' }}>
                  A complete 40-week curriculum with parent scripts, daily practice activities, 
                  and regulation tools designed specifically for age {book.age}.
                </p>
                <div className="flex gap-4 flex-wrap">
                  {book.age <= 10 ? (
                    <a
                      href={book.amazonLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-primary flex items-center gap-2">
                      Buy Now <ExternalLink size={18} />
                    </a>
                  ) : (
                    <button
                      onClick={() => setIsNotifyModalOpen(true)}
                      className="btn-primary flex items-center gap-2">
                      Notify Me When Available
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What You'll Teach Section */}
      <section className="section" style={{ background: 'var(--bg-page)' }}>
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <div className="card">
              <div className="flex items-center gap-3 mb-6">
                <BookOpen size={28} style={{ color: 'var(--brand-primary)' }} />
                <h2 className="heading-2">What You'll Teach This Year</h2>
              </div>
              <p className="body-large mb-6" style={{ color: 'var(--text-secondary)' }}>
                {bookDetails ? bookDetails.intro : `The Age ${book.age} curriculum focuses on ${book.focus.toLowerCase()}, with weekly targets that build progressively throughout the 40-week sequence.`}
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 rounded-lg" style={{ background: 'var(--bg-section)' }}>
                  <h3 className="heading-3 mb-2">Sample Topics Include:</h3>
                  <ul className="space-y-2 body-small" style={{ color: 'var(--text-secondary)' }}>
                    {bookDetails ? (
                      bookDetails.topics.map((topic, index) => (
                        <li key={index}>• {topic}</li>
                      ))
                    ) : (
                      <>
                        <li>• Recognizing and naming emotions</li>
                        <li>• Body awareness and regulation</li>
                        <li>• Communication during conflict</li>
                        <li>• Repair and apology skills</li>
                        <li>• Boundary setting and respect</li>
                      </>
                    )}
                  </ul>
                </div>
                
                <div className="p-4 rounded-lg" style={{ background: 'var(--bg-section)' }}>
                  <h3 className="heading-3 mb-2">Skills Practiced:</h3>
                  <ul className="space-y-2 body-small" style={{ color: 'var(--text-secondary)' }}>
                    {bookDetails ? (
                      bookDetails.skills.map((skill, index) => (
                        <li key={index}>• {skill}</li>
                      ))
                    ) : (
                      <>
                        <li>• Daily feeling check-ins</li>
                        <li>• Calm-down tool practice</li>
                        <li>• Scenario-based skill application</li>
                        <li>• Parent-child dialogue practice</li>
                        <li>• Weekly reflection and wrap-up</li>
                      </>
                    )}
                  </ul>
                </div>
              </div>

              {bookDetails && bookDetails.units && (
                <div className="mt-6 p-4 rounded-lg" style={{ background: 'var(--bg-section)' }}>
                  <h3 className="heading-3 mb-3">40-Week Unit Progression:</h3>
                  <ol className="space-y-2 body-small list-decimal list-inside" style={{ color: 'var(--text-secondary)' }}>
                    {bookDetails.units.map((unit, index) => (
                      <li key={index}>{unit}</li>
                    ))}
                  </ol>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* What You'll Practice Section */}
      <section className="section" style={{ background: 'var(--bg-section)' }}>
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <div className="card">
              <div className="flex items-center gap-3 mb-6">
                <Calendar size={28} style={{ color: 'var(--brand-orange)' }} />
                <h2 className="heading-2">What You'll Practice</h2>
              </div>
              <p className="body-large mb-6" style={{ color: 'var(--text-secondary)' }}>
                Each week includes 5 short lessons (Monday-Friday). 
                Lessons take about 5-10 mins and are designed for real family life.
              </p>
              
              <div className="space-y-4">
                <div className="p-4 rounded-lg" style={{ background: 'var(--bg-section)' }}>
                  <h4 className="heading-3 mb-2">Daily Lesson Style</h4>
                  <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                    Brief introduction, guided practice activity, real-world application scenario, 
                    and parent script for that situation.
                  </p>
                </div>
                
                <div className="p-4 rounded-lg" style={{ background: 'var(--bg-section)' }}>
                  <h4 className="heading-3 mb-2">Time Commitment</h4>
                  <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                    5-10 mins daily or 1 longer session per week. Flexible pacing 
                    lets you go at your child's speed.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Scripts Included Section */}
      <section className="section" style={{ background: 'var(--bg-page)' }}>
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <div className="card">
              <div className="flex items-center gap-3 mb-6">
                <MessageSquare size={28} style={{ color: 'var(--brand-success)' }} />
                <h2 className="heading-2">Parent Scripts Included</h2>
              </div>
              <p className="body-large mb-6" style={{ color: 'var(--text-secondary)' }}>
                The book includes exact language to use in real moments—no guessing, 
                no searching for the right words.
              </p>
              
              {bookDetails && bookDetails.scripts ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 rounded-lg" style={{ background: 'var(--accent-blue-200)' }}>
                    <h4 className="body-medium font-semibold mb-2">Calm Voice Scripts</h4>
                    <ul className="space-y-2 body-small" style={{ color: 'var(--text-secondary)' }}>
                      {bookDetails.scripts.calmVoice.map((script, index) => (
                        <li key={index}>• {script}</li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="p-4 rounded-lg" style={{ background: 'var(--accent-orange-200)' }}>
                    <h4 className="body-medium font-semibold mb-2">Boundary Phrases</h4>
                    <ul className="space-y-2 body-small" style={{ color: 'var(--text-secondary)' }}>
                      {bookDetails.scripts.boundary.map((script, index) => (
                        <li key={index}>• {script}</li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="p-4 rounded-lg" style={{ background: 'var(--accent-green-200)' }}>
                    <h4 className="body-medium font-semibold mb-2">Repair Language</h4>
                    <ul className="space-y-2 body-small" style={{ color: 'var(--text-secondary)' }}>
                      {bookDetails.scripts.repair.map((script, index) => (
                        <li key={index}>• {script}</li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="p-4 rounded-lg" style={{ background: 'var(--accent-pink-200)' }}>
                    <h4 className="body-medium font-semibold mb-2">Conflict Scripts</h4>
                    <ul className="space-y-2 body-small" style={{ color: 'var(--text-secondary)' }}>
                      {bookDetails.scripts.conflict.map((script, index) => (
                        <li key={index}>• {script}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 rounded-lg" style={{ background: 'var(--accent-blue-200)' }}>
                    <h4 className="body-medium font-semibold mb-2">Calm Voice Scripts</h4>
                    <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                      "I can see you're upset. Let's take some breaths together."
                    </p>
                  </div>
                  
                  <div className="p-4 rounded-lg" style={{ background: 'var(--accent-orange-200)' }}>
                    <h4 className="body-medium font-semibold mb-2">Boundary Phrases</h4>
                    <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                      "I hear you want that, and the answer is still no."
                    </p>
                  </div>
                  
                  <div className="p-4 rounded-lg" style={{ background: 'var(--accent-green-200)' }}>
                    <h4 className="body-medium font-semibold mb-2">Repair Language</h4>
                    <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                      "I made a mistake. Here's what I'll do differently."
                    </p>
                  </div>
                  
                  <div className="p-4 rounded-lg" style={{ background: 'var(--accent-pink-200)' }}>
                    <h4 className="body-medium font-semibold mb-2">Conflict Scripts</h4>
                    <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                      "Tell me what happened. I'm listening without judging."
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Navigation to Other Ages */}
      <section className="section" style={{ background: 'var(--bg-page)' }}>
        <div className="container">
          <div className="max-w-4xl mx-auto">
            {/* Book Reviews */}
            <BookReviews age={book.age} />
            
            {/* Navigation */}
            <div className="flex justify-between items-center mt-12 pt-8 border-t" style={{ borderColor: 'var(--border-light)' }}>
              {previousBook ?
              <Link to={`/book/${previousBook.age}`} className="btn-secondary flex items-center gap-2">
                  <ArrowLeft size={18} />
                  Age {previousBook.age}
                </Link> :

              <div></div>
              }
              
              <Link to="/books" className="btn-secondary">
                View All Books
              </Link>
              
              {nextBook ? (
                <Link to={`/book/${nextBook.age}`} className="btn-secondary flex items-center gap-2">
                  Age {nextBook.age}
                  <ArrowRight size={18} />
                </Link>
              ) : (
                <div></div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Notify Me Modal */}
      <NotifyMeModal 
        isOpen={isNotifyModalOpen} 
        onClose={() => setIsNotifyModalOpen(false)}
        initialAge={book.age}
      />
    </div>
  );
};

export default BookDetailPage;